import axios from 'axios';
import Config from '../config/index';
import { firebaseAppQP } from './firebaseQP';
import store from '../store';
import { clearAuthState } from '../slices/auth/authSlice';

const LOGIN_ROUTE = '/account/login';
const isOnLoginRoute = () => window.location.pathname.startsWith(LOGIN_ROUTE);

let refreshingPromise: Promise<string> | null = null;

type ApiConfig = {
  url: string;
  timeout: number;
};

export const DEFAULT_API_CONFIG: ApiConfig = {
  url: Config.API_URL,
  timeout: 60000
};

const axiosInst = axios.create({
  baseURL: DEFAULT_API_CONFIG.url,
  timeout: DEFAULT_API_CONFIG.timeout
});

axiosInst.interceptors.request.use(async (config) => {
  const user = firebaseAppQP.auth().currentUser;
  if (user) {
    config.headers = config.headers || {};
    const token = await user.getIdToken();

    config.headers['authorizationqp'] = `Bearer ${token}`;
    
  }
  return config;
});

axiosInst.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config as any;
    const url: string = originalRequest?.url || '';

    // Evita bucles: no interceptar endpoints de autenticación
    const isAuthRoute = (() => {
      try {
        const u = new URL(url || '', DEFAULT_API_CONFIG.url);
        return (
          u.pathname.startsWith('/auth/') ||
          u.hostname === 'securetoken.googleapis.com'
        );
      } catch {
        return false;
      }
    })();

    // Manejo centralizado sólo para 401 y si aún no se reintentó
    if (status === 401 && !isAuthRoute && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const user = firebaseAppQP.auth().currentUser;
        if (!user) {
          store.dispatch(clearAuthState());
          try {
            await firebaseAppQP.auth().signOut();
          } catch {}
          if (!isOnLoginRoute()) {
            window.location.assign(LOGIN_ROUTE);
          }
          throw error;
        }

        // Un único refresh compartido para todas las 401 concurrentes
        if (!refreshingPromise) {
          refreshingPromise = user.getIdToken(true).finally(() => {
            refreshingPromise = null;
          });
        }

        const newToken = await refreshingPromise;

        // Actualiza credenciales y reintenta la solicitud original
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['authorizationqp'] = `Bearer ${newToken}`;

        return await axiosInst(originalRequest);
      } catch (e) {
        // Refresh no disponible o fallido: cerrar sesión y redirigir
        store.dispatch(clearAuthState());
        try {
          await firebaseAppQP.auth().signOut();
        } catch {}
        if (!isOnLoginRoute()) {
          window.location.assign(LOGIN_ROUTE);
        }
        throw e;
      }
    }

    // Cualquier otro error, sigue su curso
    throw error;
  }
);

export default axiosInst;
